import React, { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";
import { LANGS } from "../i18n";
import { useApp } from "../context/AppContext";

/**
 * First-visit language nudge as a subtle, dismissible top banner.
 * Fires ONLY when: (a) no ?lang= URL override, (b) localStorage flag unset,
 * (c) current lang is ENG, (d) navigator.language maps to a supported non-English code.
 * Persists dismissal in localStorage.suite_lang_nudge_v1 so it never re-fires.
 * Auto-hides after 20s of no interaction, but only if the user hasn't hovered it.
 */
const BROWSER_LANG_MAP = {
  en: "ENG", fr: "FRE", ar: "ARA", zh: "CHI", de: "GER", hi: "HIN", ja: "JAP",
  ko: "KOR", pt: "POR", ru: "RUS", es: "SPA", sw: "SWA", tl: "TGL", fil: "TGL",
  ur: "URD", yo: "YOR",
};

function detectBrowserLang() {
  try {
    const raw = (navigator.languages && navigator.languages[0]) || navigator.language || "";
    const base = String(raw).toLowerCase().split(/[-_]/)[0];
    return BROWSER_LANG_MAP[base] || null;
  } catch { return null; }
}

export default function LanguageNudge() {
  const { lang, chooseLang } = useApp() || {};
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);
  const [suggested, setSuggested] = useState(null);

  const dismiss = useCallback(() => {
    try { localStorage.setItem("suite_lang_nudge_v1", "1"); } catch { /* noop */ }
    setVisible(false);
  }, []);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("lang")) return;
      if (localStorage.getItem("suite_lang_nudge_v1")) return;
      if (lang && lang !== "ENG") return;
      const code = detectBrowserLang();
      if (!code || code === "ENG") return;
      const meta = LANGS.find((x) => x.code === code);
      if (!meta) return;
      setSuggested(meta);
      // Small delay so the banner slides in after cookie consent + initial paint.
      const showTimer = setTimeout(() => {
        setVisible(true);
        // Trigger enter animation after mount
        requestAnimationFrame(() => setEntered(true));
      }, 900);
      // Auto-hide after 25s so it never blocks content forever.
      const hideTimer = setTimeout(() => dismiss(), 25000);
      return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
    } catch { /* noop */ }
  }, [lang, dismiss]);

  if (!visible || !suggested) return null;

  return (
    <div
      className={`fixed inset-x-0 top-0 z-[9998] flex justify-center px-3 pt-2 transition-transform duration-500 ease-out ${entered ? "translate-y-0" : "-translate-y-full"}`}
      data-testid="lang-nudge-banner"
      role="region"
      aria-label="Language suggestion"
    >
      <div className="pointer-events-auto flex w-full max-w-3xl items-center gap-3 rounded-full border border-black/10 bg-white/90 px-4 py-2 shadow-lg backdrop-blur-md dark:border-white/10 dark:bg-[#1a1a1a]/90">
        <span className="hidden shrink-0 text-lg sm:inline" aria-hidden="true">🌐</span>
        <div className="flex-1 text-sm">
          <span className="font-medium">View this site in {suggested.native}?</span>
          <span className="ms-2 opacity-70">
            Your browser prefers {suggested.label}. You can switch anytime from the language picker.
          </span>
        </div>
        <button
          type="button"
          onClick={() => { dismiss(); chooseLang && chooseLang(suggested.code); }}
          className="whitespace-nowrap rounded-full bg-[var(--primary)] px-3 py-1 text-xs font-semibold text-white transition-opacity hover:opacity-90"
          data-testid="lang-nudge-switch"
        >
          Switch to {suggested.native}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="whitespace-nowrap rounded-full px-2 py-1 text-xs opacity-70 hover:opacity-100"
          data-testid="lang-nudge-keep"
        >
          Keep English
        </button>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="rounded-full p-1 opacity-50 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
          data-testid="lang-nudge-close"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
